import React from 'react';
import styled, { css } from 'styled-components';
import { invertColorVariables } from '../utils/stylesMixins';
import { VIEWPORT } from '../settings/Global';
import { TEST_ID } from '../settings/E2e';
import { LOZENGE_BORDER_RADIUS } from './Lozenge';
import getCssFromRenderInfo from '../utils/getCssFromRenderInfo';
import {
    type FontFamilyGroup,
    useStaticFontMetricOffsets,
} from './PageContext';
import {
    FontMetricOffsets,
    FontMetricsCalculator,
} from '../utils/fontMetricsCalculator';
import type { FontMetricsFragment } from '../gql/api-ssr';
import type { FontMetricsFragmentFragment } from '../gql/api-public';
type FontFamily = FontFamilyGroup['fontFamilies'][number];
type FontStyle = FontFamily['fontStyles'][number];

type UnionFontMetrics = FontMetricsFragment | FontMetricsFragmentFragment;

const TITLE_LINE_HEIGHT = 1;

const fontStylePlainTitleStyles = css`
    line-height: 1;
    margin-top: 0.25em;

    @media screen and (max-width: ${VIEWPORT.MOBILE}px) {
        line-height: var(--lineHeightBody2);
        margin-top: 0;
    }
`;

const TitlePart = styled.span`
    color: var(--foregroundColorMix7);
`;

const Subtitle = styled.div`
    font-size: var(--fontSizeFixed2);
    color: var(--foregroundColorMix5);
    line-height: var(--lineHeightBody2);
    white-space: pre-wrap;
`;

const Container = styled.div<{
    $isSelected?: boolean;
}>`
    ${({ $isSelected }): ReturnType<typeof css> | null =>
        $isSelected ? invertColorVariables : null};
    display: grid;
    grid-template-columns: 100%;
    grid-gap: 1px;
`;

const Label = styled.label<{
    $isHoveredFont: boolean;
    $isSelected: boolean;
    $font: FontFamilyGroup | FontFamily | FontStyle;
}>`
    cursor: pointer;
    padding: ${({ $font }): string =>
            $font.__typename === 'CMS_SSR_FontStyleInterface'
                ? 'var(--spacing2)'
                : 'var(--spacing3)'}
        var(--spacing3);
    border-radius: ${LOZENGE_BORDER_RADIUS}px;
    position: relative;
    width: 100%;
    height: 100%; /* some items break to two lines which makes them taller. */
    color: var(--foregroundColor);
    background-color: var(--menuBackgroundColor);

    @media screen and (max-width: ${VIEWPORT.MOBILE}px) {
        padding: var(--spacing2) var(--spacing2);
    }

    ${({ $isHoveredFont }): ReturnType<typeof css> | null =>
        $isHoveredFont
            ? css`
                  background-color: var(--foregroundColorMix7_5);

                  ${TitlePart}, ${Subtitle} {
                      color: var(--foregroundColor);
                  }
              `
            : null}

    ${({ $isSelected }): ReturnType<typeof css> | null =>
        $isSelected
            ? css`
                  background-color: var(--backgroundColor);
                  color: var(--foregroundColor);

                  ${TitlePart}, ${Subtitle} {
                      color: var(--foregroundColor);
                  }
              `
            : null}
`;

const SubTitleWrapper = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin-top: var(--spacing1);
`;

const FontStyleName = styled.div`
    font-size: var(--fontSizeFixed2);
    ${fontStylePlainTitleStyles};
`;

const TitleWrapper = styled.div<{
    $font: FontFamilyGroup | FontFamily | FontStyle;
    $soehneMetricOffsets?: FontMetricOffsets;
}>`
    color: var(--foregroundColor);
    ${({ $font, $soehneMetricOffsets }): ReturnType<typeof css> | null =>
        $font.__typename === 'CMS_SSR_FontStyleInterface'
            ? css`
                  display: flex;
                  align-items: center;
                  column-gap: var(--spacing2);
                  font-size: var(--fontSizeFixed5);

                  @media screen and (max-width: ${VIEWPORT.MOBILE}px) {
                      align-items: baseline;
                  }

                  ${SubTitleWrapper} {
                      margin-top: 0;
                      width: 100%;

                      @media screen and (max-width: ${VIEWPORT.MOBILE}px) {
                          flex-wrap: wrap;

                          ${$soehneMetricOffsets
                              ? css`
                                    row-gap: var(--spacing1);
                                `
                              : null}
                      }
                  }

                  ${Subtitle} {
                      ${fontStylePlainTitleStyles};
                      @media screen and (max-width: ${VIEWPORT.MOBILE}px) {
                          flex-grow: 100;
                          flex-basis: 100%;
                          line-height: var(--lineHeightBody2);
                          margin-top: 0;

                          ${$soehneMetricOffsets
                              ? css`
                                    margin-top: ${-$soehneMetricOffsets.ascent}em;
                                    margin-bottom: ${$soehneMetricOffsets.baselineFromBottom}em;
                                `
                              : null}
                      }
                  }

                  ${FontStyleName} {
                      @media screen and (max-width: ${VIEWPORT.MOBILE}px) {
                          ${$soehneMetricOffsets
                              ? css`
                                    margin-top: ${-$soehneMetricOffsets.ascent}em;
                                    margin-bottom: ${$soehneMetricOffsets.baselineFromBottom}em;
                                `
                              : null}
                      }
                  }
              `
            : $font.__typename === 'CMS_SSR_FontFamilyGroupInterface'
              ? css`
                    font-size: var(--fontSizeFixed7);
                `
              : css`
                    font-size: var(--fontSizeFixed6);
                `};
`;

const Title = styled.div<{
    $font: FontFamilyGroup | FontFamily | FontStyle;
    $metricOffsets?: FontMetricOffsets;
}>`
    line-height: ${TITLE_LINE_HEIGHT};
    ${({ $font }): ReturnType<typeof css> | null =>
        $font.cssRenderInfo ? getCssFromRenderInfo($font.cssRenderInfo) : null};

    ${({ $metricOffsets }): ReturnType<typeof css> | null =>
        $metricOffsets
            ? css`
                  /* Make the height equal to the cap height */
                  margin-top: ${-$metricOffsets.capHeight}em;
                  margin-bottom: ${$metricOffsets.baselineFromBottom}em;
              `
            : null};
`;

const ChildrenWrapper = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-gap: 1px;
`;

const HiddenInput = styled.input`
    display: none;
`;

function FontSelectorInput({
    font,
    fontMetrics,
    isSelected,
    onChange,
    children,
    isHoveredFont,
    setHoveredFont,
}: {
    font: FontFamilyGroup | FontFamily | FontStyle;
    fontMetrics?: UnionFontMetrics | null;
    isSelected: boolean;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    children?: React.ReactNode;
    isHoveredFont: boolean;
    setHoveredFont: React.Dispatch<React.SetStateAction<string | undefined>>;
}): React.ReactElement {
    const staticFontMetrics = useStaticFontMetricOffsets();
    const [isHovered, setIsHovered] = React.useState<boolean>(false);
    const [isFocussed, setIsFocussed] = React.useState<boolean>(false);

    const onFocus = React.useCallback((): void => setIsFocussed(true), []);
    const onBlur = React.useCallback((): void => setIsFocussed(false), []);
    const onMouseEnter = React.useCallback((): void => setIsHovered(true), []);
    const onMouseLeave = React.useCallback((): void => setIsHovered(false), []);
    const timeoutRef = React.useRef<number | null>(null);

    React.useEffect((): void => {
        if (!isHovered && !isFocussed) {
            // Set the 'active' product to undefined, but give the browser a
            // chance to figure out whether the user has already set
            // 'active' to a different product. If that's the case, then we
            // want to bail out.
            timeoutRef.current = window.setTimeout((): void => {
                setHoveredFont(
                    (state: string | undefined): string | undefined => {
                        if (state === font.id) {
                            return undefined;
                        } else return state;
                    },
                );
            }, 100);
        } else {
            // If we quickly blur and then focus, then we want the browser to
            // bail out of setting the 'active' product to undefined.
            if (timeoutRef.current) {
                window.clearTimeout(timeoutRef.current);
            }
            setHoveredFont((): string => font.id);
        }
    }, [isHovered, isFocussed, timeoutRef, font, setHoveredFont]);

    const fontStyleName = React.useMemo(() => {
        if (font.__typename !== 'CMS_SSR_FontStyleInterface') {
            return;
        }
        if (
            !font.fontSubfamily ||
            !font.name.startsWith(font.fontSubfamily.name)
        ) {
            return font.name;
        }
        // Strip the subfamily name
        return font.name.substring(font.fontSubfamily.name.length).trimStart();
    }, [font]);

    const metricOffsets = React.useMemo(() => {
        if (!fontMetrics) {
            return;
        }
        return new FontMetricsCalculator({
            lineHeight: TITLE_LINE_HEIGHT,
            unitsPerEm: fontMetrics.unitsPerEmHead,
            capHeight: fontMetrics.capHeightCalc,
            ascentCalc: fontMetrics.ascentCalc,
            ascent: fontMetrics.ascentHhea,
            descentCalc: fontMetrics.descentCalc,
            descent: fontMetrics.descentHhea,
            lineGap: fontMetrics.lineGapHhea,
        }).calculate();
    }, [font]);

    return (
        <Container
            $isSelected={isSelected}
            data-cy={TEST_ID.BUY_FONT_CONTAINER}
        >
            <Label
                htmlFor={font.id}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                onFocus={onFocus}
                onBlur={onBlur}
                $isHoveredFont={isHoveredFont}
                $isSelected={isSelected}
                $font={font}
                data-cy={TEST_ID.BUY_FONT_BUTTON}
                data-cy-font-name={font.productName}
            >
                <HiddenInput
                    checked={isSelected}
                    onChange={onChange}
                    id={font.id}
                    data-font-id={font.id}
                    type="checkbox"
                />
                <TitleWrapper
                    $font={font}
                    $soehneMetricOffsets={
                        font.__typename === 'CMS_SSR_FontStyleInterface'
                            ? staticFontMetrics.SOEHNE_BUCH.BODY_2
                            : undefined
                    }
                >
                    {font.__typename === 'CMS_SSR_FontStyleInterface' ? (
                        <>
                            <Title
                                data-cy={TEST_ID.BUY_FONT_TITLE}
                                $font={font}
                                $metricOffsets={metricOffsets}
                            >
                                <TitlePart>Aa</TitlePart>
                            </Title>
                            <SubTitleWrapper>
                                <FontStyleName>{fontStyleName}</FontStyleName>
                                {/* Sub families */}
                                {font.productSubtitle ? (
                                    <Subtitle>{font.productSubtitle}</Subtitle>
                                ) : null}
                            </SubTitleWrapper>
                        </>
                    ) : (
                        <Title data-cy={TEST_ID.BUY_FONT_TITLE} $font={font}>
                            <>
                                {font.name}{' '}
                                <TitlePart>
                                    {font.__typename ===
                                    'CMS_SSR_FontFamilyGroupInterface'
                                        ? 'Collection'
                                        : 'Family'}
                                </TitlePart>
                            </>
                        </Title>
                    )}
                </TitleWrapper>
                {font.__typename !== 'CMS_SSR_FontStyleInterface' &&
                font.productSubtitle ? (
                    <SubTitleWrapper>
                        <Subtitle>{font.productSubtitle}</Subtitle>
                    </SubTitleWrapper>
                ) : null}
            </Label>
            {children && (
                <ChildrenWrapper data-cy={TEST_ID.BUY_FONT_CHILDREN}>
                    {children}
                </ChildrenWrapper>
            )}
        </Container>
    );
}

export default React.memo(FontSelectorInput);
